import { useCallback, useEffect, useState } from 'react';
import { RealtimeEvent, type NotificationFeed } from '@nbh/shared';
import { api } from '../lib/api';
import { getSocket } from './useRealtime';
import { setAppBadge } from '../lib/appBadge';

const EMPTY: NotificationFeed = { items: [], unread: 0 };

/**
 * The notification feed and its unread count (decision D30).
 *
 * Re-reads on every socket nudge rather than incrementing a local counter. The server
 * event deliberately carries no payload: an increment desyncs permanently the first
 * time a frame is missed on hotel wifi, and a badge that is quietly wrong is worse
 * than one that is briefly late.
 */
export function useNotifications() {
  const [feed, setFeed] = useState<NotificationFeed>(EMPTY);

  const refresh = useCallback(() => {
    api
      .listNotifications()
      .then((next) => {
        setFeed(next);
        setAppBadge(next.unread);
      })
      // Silent: the bell is ambient. A failed refresh must not put an error banner
      // over whatever the operator is actually doing.
      .catch(() => undefined);
  }, []);

  useEffect(refresh, [refresh]);

  useEffect(() => {
    const socket = getSocket();
    socket.on(RealtimeEvent.Notification, refresh);

    return () => {
      socket.off(RealtimeEvent.Notification, refresh);
    };
  }, [refresh]);

  /** Opening the drawer is what reading it means. */
  const markAllRead = useCallback(async () => {
    // Cleared locally first so the badge goes the moment it is tapped, then confirmed
    // by the refresh the server's own broadcast triggers.
    setFeed((current) => ({ ...current, unread: 0 }));
    setAppBadge(0);
    await api.markNotificationsRead().catch(() => undefined);
    refresh();
  }, [refresh]);

  return { feed, refresh, markAllRead };
}
